'use client';

import { useEffect, useState } from 'react';

/**
 * The hero's looping background video, mounted only where it is worth its bytes.
 *
 * Nothing is server-rendered here: the poster (painted by HeroClassic as a background)
 * is the page's Largest Contentful Paint, and it must not wait behind a video. After
 * the page has loaded, and only on a desktop-width viewport without Data Saver, the
 * <video> mounts and cross-fades over the poster. Phones never fetch the file — a
 * 12 MB hero loop over a mobile connection was the whole of a 27-second LCP.
 */
export const HERO_VIDEO_MIN_WIDTH_PX = 768;

export function shouldPlayHeroVideo(env: {
  innerWidth: number;
  matchesReducedMotion?: boolean;
  saveData?: boolean;
}): boolean {
  if (env.innerWidth < HERO_VIDEO_MIN_WIDTH_PX) return false;
  if (env.saveData) return false;
  if (env.matchesReducedMotion) return false;
  return true;
}

export function HeroVideo({ src, poster }: { src: string; poster?: string }) {
  const [play, setPlay] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const decide = (): boolean =>
      shouldPlayHeroVideo({
        innerWidth: window.innerWidth,
        matchesReducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
        saveData: (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ?? false,
      });
    let cancelled = false;
    const start = (): void => {
      if (cancelled || !decide()) return;
      // Let the first paint and the poster settle before the download starts.
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
      if (idle) idle(() => { if (!cancelled) setPlay(true); });
      else setTimeout(() => { if (!cancelled) setPlay(true); }, 800);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => { cancelled = true; window.removeEventListener('load', start); };
  }, []);

  if (!play) return null;
  return (
    <video
      className="absolute inset-0 h-full w-full object-cover"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      poster={poster || undefined}
      aria-hidden="true"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}
